import React, { useState, useEffect } from "react";
import { X, Building2 } from "lucide-react";
import { toast, Toaster } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Enquiry catalogue — each type maps to a business SKU.
 *   csr                    → CSR Partnership (society / children titles)
 *   corporate_learning     → Corporate Learning Kit (business / self-help)
 *   school_set             → School Set — 30 copies (children)
 *   corporate_gifting      → Corporate Gifting bundle (flagship / any)
 *   institutional_standing → Institutional Standing Order (geo-politics / libraries)
 *   bundle                 → Category bundle
 */
export const ENQUIRY_TYPES = {
  csr: {
    label: "CSR Partnership",
    tagline: "Underwrite a print run. Get PAN 80G receipt. Distribute to a cause.",
    defaultQty: 5000,
    fields: { showGst: true, showPan: true, showDedication: true, showBudget: true },
    valueProp: "Companies underwrite a full print run (2,000 – 25,000 copies), get a dedication page + PAN 80G receipt, and distribute to labour ministries / RWAs / worker unions / schools of your choice.",
  },
  corporate_learning: {
    label: "Corporate Learning Kit",
    tagline: "Bulk copies + facilitator's guide + private author webinar.",
    defaultQty: 50,
    fields: { showGst: true, showPo: true, showBudget: true },
    valueProp: "25 – 500 copies + a printed facilitator's guide + a private 60-minute webinar with the author for your L&D cohort. Typical use: leadership retreats, sales offsites, ELP programmes.",
  },
  school_set: {
    label: "School Set — 30 copies",
    tagline: "Classroom set + GST invoice + teaching notes.",
    defaultQty: 30,
    fields: { showGst: true, showPo: true },
    valueProp: "A 30-copy classroom set with GST invoice, PO field, and CBH teaching notes. Approved by principals across 40+ CBSE / ICSE / IB schools.",
  },
  corporate_gifting: {
    label: "Corporate Gifting",
    tagline: "Custom foil-stamped dedication. Diwali / embassy / boardroom.",
    defaultQty: 50,
    fields: { showGst: true, showDedication: true },
    valueProp: "Bulk deluxe hardcovers with a custom foil-stamped dedication page — perfect for Diwali gifting, embassy events, board offsites, and shareholder mailers.",
  },
  institutional_standing: {
    label: "Institutional Standing Order",
    tagline: "For embassies, think-tanks, and university libraries.",
    defaultQty: 100,
    fields: { showGst: true, showPo: true },
    valueProp: "Standing order for embassies, think-tanks, and university libraries — one PO covers every CBH release in your chosen series, dispatched on publication day.",
  },
  bundle: {
    label: "Weekend Read Bundle",
    tagline: "Any 2 self-help titles at ₹599 (list ₹678).",
    defaultQty: 1,
    fields: {},
    valueProp: "Pick any 2 titles from the Conlang Wellbeing shelf. Ships as a hand-tied ivory-linen sleeve. Perfect for a weekend gift or a personal reset.",
  },
};

const initial = (type) => ({
  enquiry_type: type,
  full_name: "",
  email: "",
  phone: "",
  organisation: "",
  designation: "",
  city: "",
  quantity: ENQUIRY_TYPES[type]?.defaultQty || 25,
  gst_number: "",
  pan: "",
  po_reference: "",
  budget_range: "",
  dedication: "",
  book_slug: "",
  book_title: "",
  notes: "",
});

export const InstitutionalModal = ({ open, onClose, defaultType = "corporate_gifting", book }) => {
  const [type, setType] = useState(defaultType);
  const [form, setForm] = useState(initial(defaultType));
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(null);

  useEffect(() => {
    if (open) {
      setType(defaultType);
      setForm({
        ...initial(defaultType),
        book_slug: book?.slug || "",
        book_title: book?.title || "",
      });
      setSent(null);
    }
  }, [open, defaultType, book]);

  if (!open) return null;

  const meta = ENQUIRY_TYPES[type];
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form, enquiry_type: type, quantity: Number(form.quantity) || 1 };
      const { data } = await axios.post(`${API}/institutional-enquiry`, payload);
      setSent(data);
      toast.success("Enquiry received", { description: `Reference: ${data.reference}` });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-ink/60 backdrop-blur-sm p-0 sm:p-6 animate-fade-up"
      onClick={onClose}
      data-testid="institutional-modal"
    >
      <Toaster position="top-center" richColors />
      <div
        className="bg-ivory w-full max-w-3xl max-h-[92vh] overflow-y-auto shadow-2xl relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          data-testid="institutional-modal-close"
          className="absolute right-4 top-4 w-10 h-10 flex items-center justify-center text-ink hover:text-crimson z-10"
          aria-label="Close"
        >
          <X strokeWidth={1.5} className="w-5 h-5" />
        </button>

        {sent ? (
          <div className="p-10 lg:p-14" data-testid="institutional-sent">
            <div className="w-14 h-14 flex items-center justify-center bg-crimson text-ivory mb-8">
              <Building2 strokeWidth={1.25} />
            </div>
            <p className="text-[11px] uppercase tracking-[0.32em] text-crimson mb-4">Received</p>
            <h3 className="font-editorial text-4xl text-ink mb-4">Thank you.</h3>
            <p className="text-ink-muted text-lg leading-relaxed">
              Your {meta.label.toLowerCase()} enquiry has been logged with reference{" "}
              <span className="font-editorial text-crimson">{sent.reference}</span>. The
              institutional desk will respond within one business day.
            </p>
            <button
              onClick={onClose}
              data-testid="institutional-close-after-send"
              className="mt-10 inline-flex items-center gap-3 bg-crimson text-ivory px-8 py-4 text-sm uppercase tracking-[0.24em] hover:bg-crimson-dark transition-colors duration-500"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="p-8 lg:p-10 bg-ink text-ivory grain-overlay">
              <p className="text-[10px] uppercase tracking-[0.32em] text-gold-light mb-4">
                § Bulk & Institutional Orders
              </p>
              <h3 className="font-editorial text-3xl md:text-4xl leading-tight">
                {meta.label}
              </h3>
              <p className="editorial-italic text-lg text-ivory/75 mt-2">{meta.tagline}</p>
              {book && (
                <p className="mt-4 text-xs uppercase tracking-[0.22em] text-gold-light/80">
                  Title: {book.title}
                </p>
              )}
            </div>

            {/* Type picker */}
            <div className="px-8 lg:px-10 pt-6 pb-3 border-b border-crimson/15 overflow-x-auto">
              <div className="flex gap-2 min-w-max">
                {Object.entries(ENQUIRY_TYPES).map(([id, t]) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => {
                      setType(id);
                      setForm((f) => ({ ...f, enquiry_type: id, quantity: ENQUIRY_TYPES[id].defaultQty }));
                    }}
                    data-testid={`enquiry-tab-${id}`}
                    className={`px-4 py-2 text-[11px] uppercase tracking-[0.22em] border transition-colors duration-300 ${
                      type === id
                        ? "bg-crimson text-ivory border-crimson"
                        : "text-ink border-crimson/25 hover:border-crimson"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="p-8 lg:p-10">
              <p className="text-ink-muted leading-relaxed mb-8 border-l-2 border-gold pl-4 py-2">
                {meta.valueProp}
              </p>

              <form onSubmit={submit} className="space-y-5" data-testid="institutional-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label="Full name" required>
                    <input required value={form.full_name} onChange={(e) => update("full_name", e.target.value)} className={inputCls} data-testid="inst-full-name" />
                  </Field>
                  <Field label="Work email" required>
                    <input required type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className={inputCls} data-testid="inst-email" />
                  </Field>
                  <Field label="Phone">
                    <input value={form.phone} onChange={(e) => update("phone", e.target.value)} className={inputCls} data-testid="inst-phone" />
                  </Field>
                  <Field label="Designation">
                    <input value={form.designation} onChange={(e) => update("designation", e.target.value)} className={inputCls} data-testid="inst-designation" />
                  </Field>
                </div>
                <Field label="Company / Institution" required>
                  <input required value={form.organisation} onChange={(e) => update("organisation", e.target.value)} className={inputCls} data-testid="inst-organisation" />
                </Field>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label="Shipping city" required>
                    <input required value={form.city} onChange={(e) => update("city", e.target.value)} className={inputCls} data-testid="inst-city" />
                  </Field>
                  <Field label="Quantity" required>
                    <input required type="number" min="1" value={form.quantity} onChange={(e) => update("quantity", e.target.value)} className={inputCls} data-testid="inst-quantity" />
                  </Field>
                </div>

                {(meta.fields.showGst || meta.fields.showPan || meta.fields.showPo) && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    {meta.fields.showGst && (
                      <Field label="GSTIN">
                        <input value={form.gst_number} onChange={(e) => update("gst_number", e.target.value)} className={inputCls} data-testid="inst-gst" />
                      </Field>
                    )}
                    {meta.fields.showPan && (
                      <Field label="PAN (for 80G)">
                        <input value={form.pan} onChange={(e) => update("pan", e.target.value)} className={inputCls} data-testid="inst-pan" />
                      </Field>
                    )}
                    {meta.fields.showPo && (
                      <Field label="PO reference">
                        <input value={form.po_reference} onChange={(e) => update("po_reference", e.target.value)} className={inputCls} data-testid="inst-po" />
                      </Field>
                    )}
                  </div>
                )}

                {meta.fields.showBudget && (
                  <Field label="Budget range (₹)">
                    <select value={form.budget_range} onChange={(e) => update("budget_range", e.target.value)} className={inputCls} data-testid="inst-budget">
                      <option value="">Select a range</option>
                      <option>Under ₹1 lakh</option>
                      <option>₹1 – ₹5 lakh</option>
                      <option>₹5 – ₹15 lakh</option>
                      <option>₹15 – ₹40 lakh</option>
                      <option>Above ₹40 lakh</option>
                    </select>
                  </Field>
                )}

                {meta.fields.showDedication && (
                  <Field label="Custom dedication / foil-stamp text">
                    <input value={form.dedication} onChange={(e) => update("dedication", e.target.value)} className={inputCls} data-testid="inst-dedication" placeholder="e.g. Presented by Acme Foundation on the occasion of Diwali 2026" />
                  </Field>
                )}

                <Field label="Notes / special requirements">
                  <textarea rows={3} value={form.notes} onChange={(e) => update("notes", e.target.value)} className={inputCls} data-testid="inst-notes" />
                </Field>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="institutional-submit"
                  className="w-full bg-crimson text-ivory px-8 py-4 text-sm uppercase tracking-[0.24em] hover:bg-crimson-dark transition-colors duration-500 disabled:opacity-50"
                >
                  {submitting ? "Sending…" : `Send ${meta.label} Enquiry →`}
                </button>
                <p className="text-xs text-ink-muted text-center">
                  Response within 1 business day. Enquiries do not create an order.
                </p>
              </form>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const inputCls = "w-full bg-ivory border border-crimson/20 px-4 py-3 text-sm focus:outline-none focus:border-crimson transition-colors duration-300";
const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="block text-[11px] uppercase tracking-[0.22em] text-ink-muted mb-2">
      {label} {required && <span className="text-crimson">*</span>}
    </span>
    {children}
  </label>
);

/**
 * Chooses the best default enquiry type for a given book.
 */
export const defaultEnquiryTypeForBook = (book) => {
  if (!book) return "corporate_gifting";
  const g = book.genres || [];
  if (g.includes("Children")) return "school_set";
  if (g.includes("Society")) return "csr";
  if (g.includes("Business") || g.includes("Self-Help")) return "corporate_learning";
  if (g.includes("Geo-Politics")) return "institutional_standing";
  return "corporate_gifting";
};

export default InstitutionalModal;
